import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  HelpCircle, KanbanSquare, Hand, Send, CheckCircle, Coins, Trophy, Gift,
  Plus, Upload, Tags, Users, Receipt, Settings, Activity, Mail, User, Shield,
  Download, FileText, Building2, ClipboardCheck, MessageSquare,
} from "lucide-react";
import { isOwner, isDeptAdmin } from "@/lib/utils";

function Section({ icon: Icon, title, children }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 grid place-items-center text-white shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="font-display text-xl font-semibold tracking-tight">{title}</h3>
        </div>
        <div className="text-sm text-slate-700 leading-relaxed space-y-2">{children}</div>
      </CardContent>
    </Card>
  );
}

function ContributorGuide() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Section icon={Plus} title="1. Create a ticket">
        <p>
          Post via <span className="font-medium">New Request</span>. Tickets always charge your home
          department budget (you cannot pick another). Set a bounty within the department max to publish
          immediately. Above the max, the ticket waits for department-admin (or owner) bounty approval.
        </p>
      </Section>

      <Section icon={KanbanSquare} title="2. Board — Open & My request">
        <p>
          On the <Link to="/app/board" className="text-blue-600 hover:underline font-medium">Board</Link> you
          get two tabs:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium">Open</span> — solve others&apos; work: Open to claim, Claimed by me,
            Submitted, Done
          </li>
          <li>
            <span className="font-medium">My request</span> — tickets you created: Open, Active / Need review,
            Done
          </li>
        </ul>
      </Section>

      <Section icon={Hand} title="3. Claim others' requests">
        <p>
          In <span className="font-medium">Open</span>, open a card and hit{" "}
          <span className="font-medium">Claim this request</span>. You cannot claim or solve your own tickets.
        </p>
      </Section>

      <Section icon={Send} title="4. Submit your solution">
        <p>
          Once claimed, describe what you did, add links, and attach files. Submit for review. If changes are
          requested, edit and resubmit.
        </p>
      </Section>

      <Section icon={CheckCircle} title="5. Get reviewed & paid">
        <p>
          The ticket creator (or owner / department admin) reviews. When{" "}
          <span className="font-medium">approved</span>, bounty credits are added to your personal balance.
        </p>
      </Section>

      <Section icon={MessageSquare} title="Edit bounty & rejections">
        <p>
          You can edit your published ticket&apos;s bounty. If the new amount is above the department max, the
          ticket goes back to <span className="font-medium">pending approval</span>. If a bounty request is
          rejected, it appears under <span className="font-medium">My request → Done</span> as Rejected with the
          admin&apos;s message.
        </p>
      </Section>

      <Section icon={Coins} title="Credits">
        <p>
          Department budgets fund ticket bounties. Your personal{" "}
          <span className="font-medium">balance</span> is earned credits minus redemptions — separate from the
          department pool.
        </p>
      </Section>

      <Section icon={Trophy} title="Reputation & leaderboard">
        <p>
          Approved contributions build reputation. See the{" "}
          <Link to="/app/leaderboard" className="text-blue-600 hover:underline font-medium">Leaderboard</Link>.
        </p>
      </Section>

      <Section icon={Gift} title="Spend your credits">
        <p>
          Visit <Link to="/app/rewards" className="text-blue-600 hover:underline font-medium">Rewards</Link> to
          redeem catalog items or cash (minimum 100 credits).
        </p>
      </Section>

      <Section icon={Mail} title="Verify your email first">
        <p>
          Verify your email before claiming requests or redeeming rewards. Use{" "}
          <span className="font-medium">Resend email</span> if needed.
        </p>
      </Section>
    </div>
  );
}

function OwnerGuide() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Section icon={Shield} title="Owner sits above departments">
        <p>
          You have no home department. You allocate budgets, set roles, and oversee the org. You do not claim
          or solve tickets, and you do not earn solver credits.
        </p>
      </Section>

      <Section icon={KanbanSquare} title="Board: Manage + My request">
        <p>
          Your <Link to="/app/board" className="text-blue-600 hover:underline font-medium">Board</Link> has two
          tabs:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium">Manage</span> — org oversight pipeline: Open → Active → Needs bounty
            approval → Done. Filter by department anytime. You do not claim tickets from here.
          </li>
          <li>
            <span className="font-medium">My request</span> — tickets you created: Open, Active / Need review,
            Done
          </li>
        </ul>
      </Section>

      <Section icon={Building2} title="Budgets — Departments">
        <p>
          In <span className="font-medium">Owner → Budgets → Departments</span>, allocate credits and set max
          per ticket for each department. Publishing a ticket deducts its bounty from that department&apos;s
          remaining pool. As owner you can publish over-max tickets immediately (budget auto top-up if needed).
        </p>
      </Section>

      <Section icon={ClipboardCheck} title="Budgets — Request">
        <p>
          Under <span className="font-medium">Owner → Budgets → Request</span>, review department-admin budget
          increase requests. Approve to add credits to that department&apos;s pool, or reject — both require a
          reply message the requester can see.
        </p>
      </Section>

      <Section icon={Users} title="Roles & people">
        <p>
          In <span className="font-medium">Owner → Users</span>, assign a department, then set role to{" "}
          <span className="font-medium">department admin</span> or <span className="font-medium">contributor</span>.
          Department admins manage their people, max per ticket, and over-max bounty approvals.
        </p>
      </Section>

      <Section icon={Upload} title="Import contributors">
        <p>
          In <span className="font-medium">Owner → Import</span>, paste CSV (
          <code className="bg-slate-100 px-1 rounded">email,department</code>) or fill rows. Only your org domain
          is allowed. An email already registered in another department cannot be imported again.
        </p>
      </Section>

      <Section icon={Tags} title="Categories & departments">
        <p>
          Under <span className="font-medium">Owner → Categories</span>, add or remove categories and
          departments. After adding a department, assign a department admin in Users and allocate budget.
        </p>
      </Section>

      <Section icon={Gift} title="Reward catalog">
        <p>
          In <span className="font-medium">Owner → Rewards</span>, add rewards with a credit cost and stock.
        </p>
      </Section>

      <Section icon={Receipt} title="Process redemptions">
        <p>
          <span className="font-medium">Owner → Redeem activity</span> lists redemptions. Mark fulfilled or
          reject to refund credits.
        </p>
      </Section>

      <Section icon={Settings} title="Org settings">
        <p>
          Set org name, ₹ per credit for Cashify, and whether cash redemption is enabled.
        </p>
      </Section>

      <Section icon={Activity} title="Activity feed">
        <p>
          <span className="font-medium">Owner → Activity</span> shows org-wide events as readable cards (creates,
          claims, bounty approvals, budget moves, credits). Expand a card to see the ticket title, link, and
          details like feedback or amounts.
        </p>
      </Section>
    </div>
  );
}

function DeptAdminGuide() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Section icon={KanbanSquare} title="Board: Manage + My request">
        <p>
          On the <Link to="/app/board" className="text-blue-600 hover:underline font-medium">Board</Link>:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium">Manage</span> — locked to your department: Open, Active / Need
            review, Needs bounty approval, Done
          </li>
          <li>
            <span className="font-medium">My request</span> — tickets you created: Open, Active / Need review,
            Done
          </li>
        </ul>
      </Section>

      <Section icon={Coins} title="Budgets — max per ticket">
        <p>
          In <span className="font-medium">Dept Admin → Budgets</span>, set how many credits creators in your
          department can put on a ticket without approval. Higher amounts need your approval. Tickets always
          charge your department&apos;s budget.
        </p>
      </Section>

      <Section icon={ClipboardCheck} title="Request more budget">
        <p>
          On the same Budgets tab, submit a <span className="font-medium">Request increase</span> (credits +
          message) to the owner. Track status and the owner&apos;s reply in your request list. Only one pending
          request per department at a time.
        </p>
      </Section>

      <Section icon={CheckCircle} title="Approve over-max tickets">
        <p>
          Use <span className="font-medium">Dept Admin → Approvals</span> or Board → Manage →{" "}
          <span className="font-medium">Needs bounty approval</span>. Approve to publish and deduct from the
          department budget. Reject requires a message — the creator sees it as Rejected under Done with your
          feedback.
        </p>
      </Section>

      <Section icon={Users} title="Manage department people">
        <p>
          Change roles for people in your department (contributor or department admin). You cannot promote someone
          to owner. You see tickets created by your people and can review or complete them as ticket admin.
        </p>
      </Section>

      <Section icon={Upload} title="Import into your department">
        <p>
          Use <span className="font-medium">Dept Admin → Import</span> to invite people by email. Everyone is
          added to your department only. If an email already belongs to another department (or another org),
          import rejects it.
        </p>
      </Section>

      <Section icon={Activity} title="Department activity">
        <p>
          The <span className="font-medium">Activity</span> tab shows department creates, approvals, claims, and
          credit movements. Expand a card for ticket title, link, and details.
        </p>
      </Section>

      <Section icon={FileText} title="Your own requests">
        <p>
          Use <span className="font-medium">My request</span> to track tickets you created — same Open / Active
          / Need review / Done columns as contributors. Review solutions and complete them from the ticket detail.
        </p>
      </Section>
    </div>
  );
}

export default function Help() {
  const { user } = useAuth();
  const owner = isOwner(user);
  const deptAdmin = isDeptAdmin(user);
  const defaultTab = owner ? "owner" : deptAdmin ? "department-admin" : "contributor";

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">Help</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2 flex items-center gap-3">
          <HelpCircle className="h-8 w-8 text-blue-600" />
          How to use Jamoora
        </h1>
        <p className="text-slate-600 mt-1">
          Guides for every role — contributor, owner, and department admin.
        </p>
      </div>

      <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 grid place-items-center text-white shrink-0">
            <FileText className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold tracking-tight">Jamoora brochure</h3>
            <p className="text-sm text-slate-600">
              Download the PDF brochure to learn more or share Jamoora with your team.
            </p>
          </div>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="help-brochure-download">
            <a href="/Jamoora-brochure.pdf" download>
              <Download className="h-4 w-4 mr-2" /> Download brochure
            </a>
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue={defaultTab} key={defaultTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="contributor" data-testid="help-tab-contributor">
            <User className="h-4 w-4 mr-2" /> Contributor
          </TabsTrigger>
          <TabsTrigger value="owner" data-testid="help-tab-owner">
            <Shield className="h-4 w-4 mr-2" /> Owner
          </TabsTrigger>
          <TabsTrigger value="department-admin" data-testid="help-tab-department-admin">
            <Building2 className="h-4 w-4 mr-2" /> Department admin
          </TabsTrigger>
        </TabsList>
        <TabsContent value="contributor" className="mt-6">
          <ContributorGuide />
        </TabsContent>
        <TabsContent value="owner" className="mt-6">
          <OwnerGuide />
        </TabsContent>
        <TabsContent value="department-admin" className="mt-6">
          <DeptAdminGuide />
        </TabsContent>
      </Tabs>
    </div>
  );
}
